// ts-rest contract for the digital fact find (PRD #70).
//
// The fact find is the canonical client circumstances document. FCA COBS 9
// (suitability) and the Consumer Duty both presume it exists. It is the
// single authoritative artefact with a version history, an audit trail, an
// ATR score, a CFL calculation and a refresh trigger.
//
// Endpoints under /api/fact-find/*:
//   GET  /api/fact-find/clients                      — register: one row per client
//   GET  /api/fact-find/client-options               — clients for the create typeahead
//   POST /api/fact-find/create                       — manual create (existing / new client)
//   GET  /api/fact-find/:clientId                    — current fact find + versions
//   POST /api/fact-find/:clientId/draft              — create a new draft version
//   PATCH /api/fact-find/:clientId/section           — save a section's answers
//   POST /api/fact-find/:clientId/atr                — save the ATR response set
//   POST /api/fact-find/:clientId/cfl                — save the CFL inputs
//   POST /api/fact-find/:clientId/transition         — move the draft through its lifecycle
//   POST /api/fact-find/:clientId/annotation         — add an IM annotation
//   POST /api/fact-find/:clientId/refresh            — author a section-targeted refresh
//   GET  /api/fact-find/:clientId/prefill            — list ATLAS pre-fill suggestions
//   POST /api/fact-find/prefill/:suggestionId/accept — accept a pre-fill suggestion
//   POST /api/fact-find/prefill/:suggestionId/reject — reject a pre-fill suggestion
//   GET  /api/fact-find/:clientId/document           — generated-document meta (Slice 7)
//   POST /api/fact-find/:clientId/document           — generate/regenerate the filled PDF
//   GET  /api/fact-find/:clientId/document/filled     — download the filled PDF (binary,
//                                                       a plain Nest route, not ts-rest)
//   GET  /api/fact-find/:clientId/signing             — signing state (Slice 9)
//   POST /api/fact-find/:clientId/send-for-signature  — DocuSign envelope send (Slice 9)
//   POST /api/fact-find/:clientId/mark-signed         — manual mark-signed (Slice 9)
//   POST /api/fact-find/:clientId/draft-client-email  — draft a client cover note (Outlook / Gmail)
//   POST /api/fact-find/:clientId/document/upload-signed — manual signed-PDF upload
//                                                       (multipart, a plain Nest route)
//   GET  /api/fact-find/:clientId/document/signed     — download the signed PDF (binary,
//                                                       a plain Nest route, not ts-rest)

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/** Lifecycle status of a fact-find version — mirrors the state machine. */
export const factFindStatusSchema = z.enum([
    'draft',
    'submitted',
    'reviewed',
    'approved',
    'withdrawn',
]);
export type FactFindStatus = z.infer<typeof factFindStatusSchema>;

/** Coarse freshness band for the register. */
export const factFindFreshnessSchema = z.enum(['fresh', 'refresh_due', 'in_progress', 'overdue']);
export type FactFindFreshness = z.infer<typeof factFindFreshnessSchema>;

/**
 * Discriminator selecting the canonical Septima form (PRD #70 Slice 1):
 * `individual` (`suitability-fact-find.pdf`, the default) or `entity`
 * (`entity-fact-find.pdf`). Nullable on the wire — legacy rows pre-date the
 * column and the consumer treats null as `individual`.
 */
export const factFindClientTypeSchema = z.enum(['individual', 'entity']);
export type FactFindClientType = z.infer<typeof factFindClientTypeSchema>;

export const cflBandSchema = z.enum(['low', 'medium', 'high']);
export type CflBand = z.infer<typeof cflBandSchema>;

export const prefillReviewStatusSchema = z.enum(['pending', 'accepted', 'rejected']);
export type PrefillReviewStatus = z.infer<typeof prefillReviewStatusSchema>;

export const vulnerabilityDriverSchema = z.enum([
    'health',
    'life_event',
    'resilience',
    'capability',
]);
export type VulnerabilityDriver = z.infer<typeof vulnerabilityDriverSchema>;

// ---------------------------------------------------------------------------
// Response value + section state
// ---------------------------------------------------------------------------

/** A question answer — string, number, boolean or list. `null` = unanswered. */
export const factFindAnswerSchema = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.string()),
    z.null(),
]);
export type FactFindAnswer = z.infer<typeof factFindAnswerSchema>;

/** A single per-question response within a version. */
export const factFindResponseSchema = z.object({
    sectionId: z.string(),
    questionId: z.string(),
    value: factFindAnswerSchema,
    /** Where the value came from — client entry, IM entry, or ATLAS pre-fill. */
    source: z.enum(['client', 'im', 'atlas_prefill']),
    updatedAt: z.string(),
});
export type FactFindResponse = z.infer<typeof factFindResponseSchema>;

/**
 * Risk-assessment responses + the computed score for a version (the Septima
 * "Tolerance & Capacity for Risk" instrument). Responses key off question id
 * and store the SELECTED OPTION LABEL (a string); the scorer maps label →
 * score.
 */
export const atrAssessmentSchema = z.object({
    /** question id -> selected option label. */
    responses: z.record(z.string(), z.string()),
    /** Sum of the selected option scores (0..120). */
    total: z.number(),
    /** Continuous 1.0-7.0 score. */
    rawScore: z.number(),
    /** Integer 1-7 band. */
    band: z.number().int().min(1).max(7),
    /** House mandate the band maps onto. */
    mandate: z.string(),
    /** Whether all questions are answered. */
    complete: z.boolean(),
    /** Low-capacity signals for the Vulnerable Clients surface. */
    capacityFlags: z.array(z.string()),
    /** True when any capacity flag is set. */
    lowCapacity: z.boolean(),
    computedAt: z.string(),
});
export type AtrAssessment = z.infer<typeof atrAssessmentSchema>;

/** CFL inputs + the computed working for a version. */
export const cflWorkingStepSchema = z.object({
    label: z.string(),
    amount: z.number(),
    note: z.string(),
});
export type CflWorkingStep = z.infer<typeof cflWorkingStepSchema>;

export const cflCalculationSchema = z.object({
    annualIncome: z.number(),
    essentialExpenditure: z.number(),
    liquidAssets: z.number(),
    knownCommitments: z.number(),
    portfolioValue: z.number(),
    safetyBuffer: z.number(),
    netSurplus: z.number(),
    freeLiquidAssets: z.number(),
    absorbableLoss: z.number(),
    maxDrawdownPct: z.number(),
    band: cflBandSchema,
    working: z.array(cflWorkingStepSchema),
    computedAt: z.string(),
});
export type CflCalculation = z.infer<typeof cflCalculationSchema>;

/** A vulnerability marker flagged from the vulnerability-indicators section. */
export const factFindVulnerabilityMarkerSchema = z.object({
    driver: vulnerabilityDriverSchema,
    questionId: z.string(),
    detail: z.string(),
});
export type FactFindVulnerabilityMarker = z.infer<typeof factFindVulnerabilityMarkerSchema>;

/** An IM annotation against a section. */
export const factFindAnnotationSchema = z.object({
    id: z.string(),
    sectionId: z.string(),
    author: z.string(),
    note: z.string(),
    kind: z.enum(['note', 'gap', 'inconsistency']),
    createdAt: z.string(),
});
export type FactFindAnnotation = z.infer<typeof factFindAnnotationSchema>;

/** An immutable audit entry — who/what/when/why. */
export const factFindAuditEntrySchema = z.object({
    id: z.string(),
    at: z.string(),
    by: z.string(),
    action: z.string(),
    detail: z.string().optional(),
});
export type FactFindAuditEntry = z.infer<typeof factFindAuditEntrySchema>;

// ---------------------------------------------------------------------------
// FactFindVersion — an immutable historical snapshot
// ---------------------------------------------------------------------------

export const factFindVersionSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    /** Which canonical Septima form this version is for. Null ⇒ `individual`. */
    clientType: factFindClientTypeSchema.nullable(),
    /** Monotonic version number, starting at 1. */
    versionNumber: z.number().int(),
    status: factFindStatusSchema,
    /** Per-question responses. */
    responses: z.array(factFindResponseSchema),
    atr: atrAssessmentSchema.nullable(),
    cfl: cflCalculationSchema.nullable(),
    vulnerabilityMarkers: z.array(factFindVulnerabilityMarkerSchema),
    annotations: z.array(factFindAnnotationSchema),
    auditLog: z.array(factFindAuditEntrySchema),
    /** Reason this version exists — initial / refresh / annual review. */
    reason: z.string(),
    /** Section ids a refresh version targets (empty for a full fact find). */
    refreshSections: z.array(z.string()),
    createdAt: z.string(),
    updatedAt: z.string(),
    createdBy: z.string(),
    /** Set once the version reaches `approved` — then the row is immutable. */
    approvedAt: z.string().nullable(),
    approvedBy: z.string().nullable(),
});
export type FactFindVersion = z.infer<typeof factFindVersionSchema>;

// ---------------------------------------------------------------------------
// FactFind — one current record per client
// ---------------------------------------------------------------------------

export const factFindSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    /** Which canonical Septima form this fact find uses. Null ⇒ `individual`. */
    clientType: factFindClientTypeSchema.nullable(),
    im: z.string(),
    /** Id of the working (draft / submitted / reviewed) version, if any. */
    currentVersionId: z.string().nullable(),
    /** Id of the latest approved version, if any. */
    approvedVersionId: z.string().nullable(),
    /** Every version, newest first. */
    versions: z.array(factFindVersionSchema),
    /** Freshness band derived from the latest approved version age. */
    freshness: factFindFreshnessSchema,
    /** Age in days of the latest approved version (−1 if never approved). */
    ageDays: z.number().int(),
    /** Portal magic-link token for the working version. */
    portalToken: z.string(),
});
export type FactFind = z.infer<typeof factFindSchema>;

/** Register row — one per client. */
export const factFindRegisterRowSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    im: z.string(),
    freshness: factFindFreshnessSchema,
    status: factFindStatusSchema,
    completenessPct: z.number().int(),
    ageDays: z.number().int(),
    atrBand: z.number().int().nullable(),
    cflBand: cflBandSchema.nullable(),
    vulnerabilityFlag: z.boolean(),
    versionCount: z.number().int(),
    lastUpdated: z.string(),
});
export type FactFindRegisterRow = z.infer<typeof factFindRegisterRowSchema>;

// ---------------------------------------------------------------------------
// Risk summary — the approved fact find's risk score for the Vulnerable
// Clients surface (Slice 8). Sourced from the `desk`-readable FactFind, so it
// can feed the Desk Vulnerable Clients page without touching `comply`-only
// VulnerabilityRecord / VulnerabilityFlag rows.
// ---------------------------------------------------------------------------

export const factFindRiskSummaryRowSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    /** Source version's lifecycle status — approved when an approved version exists. */
    status: factFindStatusSchema,
    /** Integer 1-7 risk band, or null when the version carries no scored ATR. */
    band: z.number().int().min(1).max(7).nullable(),
    /** House mandate the band maps onto, or null when unscored. */
    mandate: z.string().nullable(),
    /** Sum of selected option scores (0..120), or null when unscored. */
    total: z.number().nullable(),
    /** Low-capacity signals (income security / emergency funds / loss tolerance / horizon). */
    capacityFlags: z.array(z.string()),
    /** True when any capacity flag is set. */
    lowCapacity: z.boolean(),
});
export type FactFindRiskSummaryRow = z.infer<typeof factFindRiskSummaryRowSchema>;

export const factFindRiskSummaryResponseSchema = z.object({
    rows: z.array(factFindRiskSummaryRowSchema),
});
export type FactFindRiskSummaryResponse = z.infer<typeof factFindRiskSummaryResponseSchema>;

// ---------------------------------------------------------------------------
// FactFindDocument — the generated, signable fact-find PDF (Slice 7)
// ---------------------------------------------------------------------------

/** Signing lifecycle of the generated document. */
export const factFindSigningStatusSchema = z.enum([
    'none',
    'sent',
    'partially_signed',
    'completed',
    'declined',
    'voided',
]);
export type FactFindSigningStatus = z.infer<typeof factFindSigningStatusSchema>;

/** A signing recipient on the generated document. */
export const factFindDocumentRecipientSchema = z.object({
    name: z.string(),
    email: z.string(),
    status: z.string(),
});
export type FactFindDocumentRecipient = z.infer<typeof factFindDocumentRecipientSchema>;

/**
 * Generated fact-find document META — the projection of a `FactFindDocument`
 * row WITHOUT the raw PDF bytes (those download via the binary route). The
 * `hasFilledPdf` / `hasSignedPdf` booleans tell the UI which downloads /
 * signing affordances to offer.
 */
export const factFindDocumentSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    factFindVersionId: z.string(),
    clientType: factFindClientTypeSchema,
    signingMethod: z.enum(['docusign', 'manual']).nullable(),
    docusignEnvelopeId: z.string().nullable(),
    signingStatus: factFindSigningStatusSchema,
    recipients: z.array(factFindDocumentRecipientSchema).nullable(),
    hasFilledPdf: z.boolean(),
    hasSignedPdf: z.boolean(),
    createdAt: z.string(),
    sentAt: z.string().nullable(),
    completedAt: z.string().nullable(),
});
export type FactFindDocument = z.infer<typeof factFindDocumentSchema>;

/** Generate / regenerate response — the document meta, or null if absent. */
export const factFindDocumentResponseSchema = z.object({
    ok: z.boolean(),
    document: factFindDocumentSchema.nullable(),
});
export type FactFindDocumentResponse = z.infer<typeof factFindDocumentResponseSchema>;

// ---------------------------------------------------------------------------
// Signing (Slice 9) — DocuSign send + manual fallback
// ---------------------------------------------------------------------------

/**
 * Signing state the client fact-find view reads to decide which signing
 * affordances to offer. `docusignConfigured` gates the "Send via DocuSign"
 * action — the manual path (download / upload-signed / mark-signed) is always
 * available. The booleans + status mirror the stored document row.
 */
export const factFindSigningStateSchema = z.object({
    /** Whether DocuSign is configured on the server (gates the send affordance). */
    docusignConfigured: z.boolean(),
    signingStatus: factFindSigningStatusSchema,
    signingMethod: z.enum(['docusign', 'manual']).nullable(),
    hasFilledPdf: z.boolean(),
    hasSignedPdf: z.boolean(),
    recipients: z.array(factFindDocumentRecipientSchema).nullable(),
});
export type FactFindSigningState = z.infer<typeof factFindSigningStateSchema>;

export const factFindSigningStateResponseSchema = z.object({
    ok: z.boolean(),
    signing: factFindSigningStateSchema,
});
export type FactFindSigningStateResponse = z.infer<typeof factFindSigningStateResponseSchema>;

/** A signer the IM supplies when sending for DocuSign signature. */
export const sendForSignatureSignerSchema = z.object({
    name: z.string().min(1),
    email: z.string().email(),
});
export type SendForSignatureSigner = z.infer<typeof sendForSignatureSignerSchema>;

export const sendForSignatureRequestSchema = z.object({
    by: z.string(),
    signers: z.array(sendForSignatureSignerSchema).min(1),
    /** Optional override for the DocuSign email subject. */
    emailSubject: z.string().optional(),
});
export type SendForSignatureRequest = z.infer<typeof sendForSignatureRequestSchema>;

export const markSignedRequestSchema = z.object({
    by: z.string(),
});
export type MarkSignedRequest = z.infer<typeof markSignedRequestSchema>;

/** Which connected mailbox the cover-note draft lands in. */
export const draftEmailProviderSchema = z.enum(['gmail', 'outlook']);
export type DraftEmailProvider = z.infer<typeof draftEmailProviderSchema>;

/**
 * Request to draft a client cover-note email into the IM's connected mailbox.
 * `by` is the IM display name used in the sign-off + audit; the mailbox token
 * is keyed by the authenticated user server-side, never by this field.
 * `provider` selects Outlook (default) or Gmail — the token for the chosen
 * provider must be connected for the signed-in user.
 */
export const draftClientEmailRequestSchema = z.object({
    by: z.string(),
    provider: draftEmailProviderSchema.default('outlook'),
});
export type DraftClientEmailRequest = z.infer<typeof draftClientEmailRequestSchema>;

export const draftClientEmailResponseSchema = z.object({
    ok: z.boolean(),
    /** Provider draft id (Gmail draft id / Graph message id). */
    draftId: z.string(),
    /** Deep link to the draft in the chosen mailbox for the IM to review + send. */
    webUrl: z.string(),
});
export type DraftClientEmailResponse = z.infer<typeof draftClientEmailResponseSchema>;

// ---------------------------------------------------------------------------
// ATLAS pre-fill suggestion
// ---------------------------------------------------------------------------

export const prefillSuggestionSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    sectionId: z.string(),
    questionId: z.string(),
    suggestedValue: factFindAnswerSchema,
    sourceCallId: z.string(),
    sourceQuote: z.string(),
    confidence: z.enum(['high', 'medium', 'low']),
    reviewStatus: prefillReviewStatusSchema,
    createdAt: z.string(),
    reviewedAt: z.string().nullable(),
    reviewedBy: z.string().nullable(),
});
export type PrefillSuggestion = z.infer<typeof prefillSuggestionSchema>;

// ---------------------------------------------------------------------------
// Request / response schemas
// ---------------------------------------------------------------------------

const factFindResponseBody = z.object({
    ok: z.boolean(),
    factFind: factFindSchema,
});

export const factFindRegisterResponseSchema = z.object({
    rows: z.array(factFindRegisterRowSchema),
});
export type FactFindRegisterResponse = z.infer<typeof factFindRegisterResponseSchema>;

export const createDraftRequestSchema = z.object({
    by: z.string(),
    reason: z.string().default('Initial fact find'),
    /** When set, the draft is a section-targeted refresh. */
    refreshSections: z.array(z.string()).default([]),
    /**
     * Which canonical Septima form the draft is for. Defaults to `individual`.
     * A refresh draft inherits the fact find's existing `clientType` and
     * ignores this. (Slice 3 surfaces the Individual / Entity choice in the UI.)
     */
    clientType: factFindClientTypeSchema.default('individual'),
});
export type CreateDraftRequest = z.infer<typeof createDraftRequestSchema>;

// ---------------------------------------------------------------------------
// Manual create flow (PRD #70 Slice 3) — start a fact find for an existing
// Client by id, or "from scratch" for a brand-new minimal Client.
// ---------------------------------------------------------------------------

/** A client option for the manual-create typeahead — identity only. */
export const factFindClientOptionSchema = z.object({
    id: z.string(),
    name: z.string(),
    /**
     * The Client's legal-entity classification, used to suggest the form tab.
     * Null when the source row carries no classification.
     */
    legalEntityType: z.string().nullable(),
    /** Whether this client already has a fact find (the register has a row). */
    hasFactFind: z.boolean(),
});
export type FactFindClientOption = z.infer<typeof factFindClientOptionSchema>;

export const factFindClientsResponseSchema = z.object({
    clients: z.array(factFindClientOptionSchema),
});
export type FactFindClientsResponse = z.infer<typeof factFindClientsResponseSchema>;

/**
 * Manual fact-find creation. The IM either points at an existing Client
 * (`clientId`) or supplies a new client's identity (`newClient`) which the
 * server materialises as a minimal Client row before opening the blank form.
 * Exactly one of the two is required — enforced by the union.
 */
export const createFactFindRequestSchema = z
    .object({
        by: z.string(),
        /** Which canonical Septima form to open — the active dialog tab. */
        clientType: factFindClientTypeSchema,
        /** Point at an existing Client. */
        clientId: z.string().min(1).optional(),
        /** ...or supply a brand-new client to materialise "from scratch". */
        newClient: z.object({ name: z.string().min(1) }).optional(),
    })
    // Exactly one of clientId / newClient — a flat object + refine keeps the
    // inferred type small (an intersection-of-union here inflates the whole
    // apiContract type past tsc's serialization limit — TS7056).
    .refine((d) => Boolean(d.clientId) !== Boolean(d.newClient), {
        message: 'Provide exactly one of clientId or newClient.',
    });
export type CreateFactFindRequest = z.infer<typeof createFactFindRequestSchema>;

/** Manual-create response — the resolved client id + the opened fact find. */
export const createFactFindResponseSchema = z.object({
    ok: z.boolean(),
    clientId: z.string(),
    factFind: factFindSchema,
});
export type CreateFactFindResponse = z.infer<typeof createFactFindResponseSchema>;

export const saveSectionRequestSchema = z.object({
    by: z.string(),
    /** Whether the actor is the client (portal) or the IM (desk). */
    actor: z.enum(['client', 'im']),
    sectionId: z.string(),
    /** question id -> answer. */
    answers: z.record(z.string(), factFindAnswerSchema),
});
export type SaveSectionRequest = z.infer<typeof saveSectionRequestSchema>;

export const saveAtrRequestSchema = z.object({
    by: z.string(),
    actor: z.enum(['client', 'im']),
    /** question id -> selected option label. */
    responses: z.record(z.string(), z.string()),
});
export type SaveAtrRequest = z.infer<typeof saveAtrRequestSchema>;

export const saveCflRequestSchema = z.object({
    by: z.string(),
    actor: z.enum(['client', 'im']),
    annualIncome: z.number(),
    essentialExpenditure: z.number(),
    liquidAssets: z.number(),
    knownCommitments: z.number(),
    portfolioValue: z.number(),
});
export type SaveCflRequest = z.infer<typeof saveCflRequestSchema>;

export const factFindTransitionRequestSchema = z.object({
    to: factFindStatusSchema,
    by: z.string(),
    note: z.string().optional(),
});
export type FactFindTransitionRequest = z.infer<typeof factFindTransitionRequestSchema>;

export const addAnnotationRequestSchema = z.object({
    by: z.string(),
    sectionId: z.string(),
    note: z.string().min(1),
    kind: z.enum(['note', 'gap', 'inconsistency']).default('note'),
});
export type AddAnnotationRequest = z.infer<typeof addAnnotationRequestSchema>;

export const refreshRequestSchema = z.object({
    by: z.string(),
    sections: z.array(z.string()).min(1),
    message: z.string().default(''),
});
export type RefreshRequest = z.infer<typeof refreshRequestSchema>;

export const refreshResponseSchema = z.object({
    ok: z.boolean(),
    factFind: factFindSchema,
    /** Stub portal URL the section refresh was sent to. */
    refreshUrl: z.string(),
});
export type RefreshResponse = z.infer<typeof refreshResponseSchema>;

export const prefillListResponseSchema = z.object({
    suggestions: z.array(prefillSuggestionSchema),
});
export type PrefillListResponse = z.infer<typeof prefillListResponseSchema>;

export const prefillActionResponseSchema = z.object({
    ok: z.boolean(),
    suggestion: prefillSuggestionSchema,
    /** Present when an accept wrote the value into a draft. */
    factFind: factFindSchema.optional(),
});
export type PrefillActionResponse = z.infer<typeof prefillActionResponseSchema>;

const errorBody = z.object({ error: z.string(), code: z.string().optional() });

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const factFindContract = c.router(
    {
        listRegister: {
            method: 'GET',
            path: '/fact-find/clients',
            query: z.object({ imId: z.string().optional() }).optional(),
            responses: { 200: factFindRegisterResponseSchema },
            summary: 'Fact-find register — one row per client',
        },
        listClientOptions: {
            method: 'GET',
            path: '/fact-find/client-options',
            query: z.object({ q: z.string().optional() }).optional(),
            responses: { 200: factFindClientsResponseSchema },
            summary: 'List clients for the manual-create typeahead',
        },
        riskSummary: {
            method: 'GET',
            path: '/fact-find/risk-summary',
            query: z.object({ imId: z.string().optional() }).optional(),
            responses: { 200: factFindRiskSummaryResponseSchema },
            summary: 'Per-client risk score (band / mandate / capacity flags) from the fact find',
        },
        createFactFind: {
            method: 'POST',
            path: '/fact-find/create',
            body: createFactFindRequestSchema,
            responses: {
                200: createFactFindResponseSchema,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Manually create a fact find for an existing or brand-new client',
        },
        getFactFind: {
            method: 'GET',
            path: '/fact-find/:clientId',
            responses: { 200: factFindResponseBody, 404: errorBody },
            summary: 'Get a client fact find with full version history',
        },
        getByToken: {
            method: 'GET',
            path: '/fact-find/token/:token',
            responses: { 200: factFindResponseBody, 404: errorBody },
            summary: 'Get a fact find by portal magic-link token',
        },
        createDraft: {
            method: 'POST',
            path: '/fact-find/:clientId/draft',
            body: createDraftRequestSchema,
            responses: { 200: factFindResponseBody, 400: errorBody, 404: errorBody },
            summary: 'Create a new draft version of the fact find',
        },
        saveSection: {
            method: 'PATCH',
            path: '/fact-find/:clientId/section',
            body: saveSectionRequestSchema,
            responses: { 200: factFindResponseBody, 400: errorBody, 404: errorBody },
            summary: 'Save a section of answers onto the working version',
        },
        saveAtr: {
            method: 'POST',
            path: '/fact-find/:clientId/atr',
            body: saveAtrRequestSchema,
            responses: { 200: factFindResponseBody, 400: errorBody, 404: errorBody },
            summary: 'Save the ATR response set and recompute the score',
        },
        saveCfl: {
            method: 'POST',
            path: '/fact-find/:clientId/cfl',
            body: saveCflRequestSchema,
            responses: { 200: factFindResponseBody, 400: errorBody, 404: errorBody },
            summary: 'Save the CFL inputs and recompute the working',
        },
        transition: {
            method: 'POST',
            path: '/fact-find/:clientId/transition',
            body: factFindTransitionRequestSchema,
            responses: {
                200: factFindResponseBody,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Move the working version through its lifecycle',
        },
        addAnnotation: {
            method: 'POST',
            path: '/fact-find/:clientId/annotation',
            body: addAnnotationRequestSchema,
            responses: { 200: factFindResponseBody, 400: errorBody, 404: errorBody },
            summary: 'Add an IM annotation against a section',
        },
        refresh: {
            method: 'POST',
            path: '/fact-find/:clientId/refresh',
            body: refreshRequestSchema,
            responses: { 200: refreshResponseSchema, 400: errorBody, 404: errorBody },
            summary: 'Author a section-targeted refresh and send the portal link',
        },
        listPrefill: {
            method: 'GET',
            path: '/fact-find/:clientId/prefill',
            responses: { 200: prefillListResponseSchema },
            summary: 'List ATLAS pre-fill suggestions for a client',
        },
        acceptPrefill: {
            method: 'POST',
            path: '/fact-find/prefill/:suggestionId/accept',
            body: z.object({ by: z.string() }),
            responses: { 200: prefillActionResponseSchema, 400: errorBody, 404: errorBody },
            summary: 'Accept a pre-fill suggestion and write it into the draft',
        },
        rejectPrefill: {
            method: 'POST',
            path: '/fact-find/prefill/:suggestionId/reject',
            body: z.object({ by: z.string() }),
            responses: { 200: prefillActionResponseSchema, 400: errorBody, 404: errorBody },
            summary: 'Reject a pre-fill suggestion',
        },
        getDocument: {
            method: 'GET',
            path: '/fact-find/:clientId/document',
            responses: { 200: factFindDocumentResponseSchema, 404: errorBody },
            summary: 'Get the generated fact-find document meta for a client',
        },
        generateDocument: {
            method: 'POST',
            path: '/fact-find/:clientId/document',
            body: z.object({ by: z.string() }),
            responses: { 200: factFindDocumentResponseSchema, 400: errorBody, 404: errorBody },
            summary: 'Generate / regenerate the filled fact-find PDF and return its meta',
        },
        getSigningState: {
            method: 'GET',
            path: '/fact-find/:clientId/signing',
            responses: { 200: factFindSigningStateResponseSchema, 404: errorBody },
            summary: 'Signing state for the client fact-find view (gates the DocuSign affordance)',
        },
        sendForSignature: {
            method: 'POST',
            path: '/fact-find/:clientId/send-for-signature',
            body: sendForSignatureRequestSchema,
            responses: {
                200: factFindSigningStateResponseSchema,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Send the filled fact-find PDF for DocuSign signature',
        },
        markSigned: {
            method: 'POST',
            path: '/fact-find/:clientId/mark-signed',
            body: markSignedRequestSchema,
            responses: { 200: factFindSigningStateResponseSchema, 400: errorBody, 404: errorBody },
            summary: 'Mark the fact-find document signed (manual, without an uploaded copy)',
        },
        draftClientEmail: {
            method: 'POST',
            path: '/fact-find/:clientId/draft-client-email',
            body: draftClientEmailRequestSchema,
            responses: {
                200: draftClientEmailResponseSchema,
                404: errorBody,
                // 409 = chosen provider not configured/connected; 422 = client has no email on file.
                409: errorBody,
                422: errorBody,
            },
            summary:
                "Draft a client cover-note email into the IM's connected mailbox (Outlook / Gmail)",
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
